/**
 * Data issues inbox API (/api/v1/admin/data-issues). apiClient resolves to the body `{ success, data }`;
 * unwrap reads .data once. decide/undo carry `mode` because 200 vs 202 is not visible here.
 */
import { apiClient } from './client'
import type { Commission } from './commissions.api'

export const DATA_ISSUES_QUERY_KEY = ['data-issues'] as const

export type DataIssueStatus = 'open' | 'applying' | 'applied' | 'kept' | 'cleared'
export type DataIssueOrigin = 'detector' | 'manual'
export type RowOutcome = 'applied' | 'no_op' | 'stale' | 'blocked' | 'failed' | 'halted' | 'undone' | 'undo_refused'
export type PlanVerdict = 'will_apply' | 'no_op' | 'blocked'
export type WarningCode =
  | 'rows_blocked' | 'cancels_bookings' | 'affiliate_notified' | 'money_relevant_hotel_field' | 'pulls_from_open_payout'
export type ManualSubjectType = 'commission' | 'hotel' | 'affiliate' | 'commission_rule'
export type SubjectType = ManualSubjectType | 'hotel_field' | 'pms_config_field' | 'feed_row' | 'pms_property'
export type EventKind =
  | 'detected' | 'reopened' | 'recurred' | 'moved' | 'superseded' | 'cleared' | 'imported'
  | 'opened_manually' | 'assigned' | 'github_linked' | 'decided' | 'undo_requested' | 'stopped'
  | 'row_applied' | 'row_not_applied' | 'row_undone' | 'row_not_undone'
export type DataIssueSort = 'money' | 'age' | 'lastSeen'
export type PresetKey =
  | 'open' | 'assignedToMe' | 'syncConflicts' | 'bookingFeedErrors' | 'notFoundInPms' | 'legacyReviewFlag' | 'pmsMapping' | 'manual'
export type FieldValues = Record<string, string | null>

export interface Pagination { page: number; limit: number; total: number; totalPages: number }
export interface MoneyAmount { currency: string; amount: number }
export interface RowCounts {
  open: number; applying: number; applied: number; kept: number; cleared: number; total: number
  /** open rows whose last attempt failed (shown red) */
  failedOpen: number
}

export interface DataIssueListItem {
  id: string
  typeKey: string
  family: string
  origin: DataIssueOrigin
  status: DataIssueStatus
  /** manual issues */
  title: string | null
  /** detector issues: params of admin:dataIssues.typeTitles.<type> */
  titleParams: Record<string, string | number>
  hotelId: string | null
  hotelName: string | null
  affiliateId: string | null
  affiliateName: string | null
  assigneeUserId: string | null
  githubIssue: number | null
  rowCounts: RowCounts
  /** open rows with a known amount, per currency */
  atStake: MoneyAmount[]
  /** open rows whose amount is unknown (revshare NULL) → "Unknown" */
  unknownAtStakeRows: number
  firstSeenAt: string
  lastSeenAt: string
  statusChangedAt: string
  /** manual: the reporter */
  createdByEmail: string | null
}

export interface DecisionSummary {
  decisionId: string
  kind: 'decided' | 'undo_requested'
  optionKey: string
  mode: 'keep' | 'inline' | 'queued'
  undoesDecisionId: string | null
  at: string
  actorEmail: string | null
  reasonCode: string | null
  note: string | null
  counts: Partial<Record<RowOutcome | 'kept' | 'pending', number>>
  /** rows still applied by this decision that an undo could reverse */
  undoableRows: number
}

export interface ActiveWork {
  decisionId: string
  kind: 'decided' | 'undo_requested'
  total: number
  remaining: number
  startedAt: string
}

export interface DataIssueDetail extends DataIssueListItem {
  causeKey: string
  description: string | null
  evidence: Record<string, unknown>
  /** newest first */
  decisions: DecisionSummary[]
  /** decisions with rows still applying (poll while non-empty) */
  activeWork: ActiveWork[]
}

export interface DataIssueRow {
  id: string
  issueId: string
  typeKey: string
  subjectType: SubjectType
  subjectKey: string
  status: DataIssueStatus
  commissionId: string | null
  hotelId: string | null
  affiliateId: string | null
  commissionRuleId: string | null
  evidence: Record<string, unknown>
  amountAtStake: number | null
  currency: string | null
  firstSeenAt: string
  lastSeenAt: string
  statusChangedAt: string
  lastOutcome: { outcome: RowOutcome; code: string | null; at: string; decisionId: string | null } | null
  /** the backup of the applied fix, when status is applied */
  applied: { decisionId: string; before: FieldValues; after: FieldValues; at: string } | null
  /** subjectType 'commission' only — the same row GET /commissions returns to admins */
  commission: Commission | null
}

export interface DataIssueEvent {
  id: string
  kind: EventKind
  rowId: string | null
  decisionId: string | null
  fromStatus: DataIssueStatus | null
  toStatus: DataIssueStatus | null
  actorType: 'admin' | 'system'
  actorEmail: string | null
  reasonCode: string | null
  reason: string | null
  payload: Record<string, unknown>
  createdAt: string
}

export interface ParamSpec { name: string; kind: 'enum'; values: string[]; required: boolean }
export interface DataIssueTypeInfo {
  key: string
  family: string
  precedence: number
  subjectTypes: SubjectType[]
  titleKey: string
  options: string[]
  fixesEnabled: boolean
  evidenceKeys: string[]
}
export interface DataIssueOptionInfo {
  key: string
  kind: 'fix' | 'keep'
  subjectType: SubjectType | '*'
  reasonCodes: string[]
  noteRule: 'always' | 'when_other'
  destructive: boolean
  undoable: boolean
  paramSpec: ParamSpec[]
}
export interface DataIssueTypesInfo {
  /** DATA_ISSUES_APPLY_ENABLED on this environment */
  applyEnabled: boolean
  types: DataIssueTypeInfo[]
  options: DataIssueOptionInfo[]
  reopenReasonCodes: string[]
}
export interface DataIssueAssignee { id: string; email: string | null; name: string | null }
export interface DataIssueAssignees { assignees: DataIssueAssignee[]; partial: boolean }

export interface DataIssueListQuery {
  type?: string
  status?: DataIssueStatus | 'all'
  /** 'me' | 'none' | <uuid> */
  assignee?: string
  origin?: DataIssueOrigin
  hotelId?: string
  affiliateId?: string
  commissionId?: string
  ruleId?: string
  sort?: DataIssueSort
  page?: number
  limit?: number
}
export interface DataIssueListPage {
  items: DataIssueListItem[]
  presetCounts: Record<PresetKey, number>
  pagination: Pagination
}
export interface DataIssueRowsQuery {
  status?: DataIssueStatus | 'all'
  outcome?: 'failed' | 'stale' | 'blocked' | 'halted' | 'undo_refused'
  page?: number
  limit?: number
}
export interface DataIssueRowsPage { rows: DataIssueRow[]; pagination: Pagination }
export interface DataIssueEventsQuery { page?: number; limit?: number; includeRowEvents?: boolean }
export interface DataIssueEventsPage { events: DataIssueEvent[]; pagination: Pagination }

export interface ManualIssueBody {
  subjectType: ManualSubjectType
  subjectIds: string[]
  title: string
  description: string
  assigneeUserId?: string | null
  githubIssue?: number | null
}

export interface RowMoney {
  currency: string | null
  affiliateId: string | null
  withdrawableNetBefore: number
  withdrawableNetAfter: number
  payoutId: string | null
  payoutStatus: string | null
}
export interface RowPlan {
  rowId: string
  verdict: PlanVerdict
  blockedCode: string | null
  before: FieldValues
  after: FieldValues
  money: RowMoney | null
  warnings: WarningCode[]
  planHash: string
}
export interface CurrencyTotal { currency: string; withdrawableNetBefore: number; withdrawableNetAfter: number; delta: number }
export interface AffiliateTotal extends CurrencyTotal { affiliateId: string; affiliateName: string | null }
export interface DecisionPreview {
  issueId: string
  optionKey: string
  /** the rows the decision covers — send them back as DecideBody.rowIds */
  rowIds: string[]
  /** null for keep */
  previewToken: string | null
  counts: { willApply: number; noOp: number; blocked: number }
  blockedByCode: Record<string, number>
  warnings: WarningCode[]
  totals: { byCurrency: CurrencyTotal[]; byAffiliate: AffiliateTotal[] }
  rows: RowPlan[]
}
export interface PreviewBody {
  optionKey: string
  params: Record<string, unknown>
  /** explicit rows; omit to mean every open row minus leaveOutRowIds */
  rowIds?: string[]
  leaveOutRowIds?: string[]
}
export interface DecideBody {
  optionKey: string
  params: Record<string, unknown>
  rowIds: string[]
  keepRowIds?: string[]
  previewToken: string | null
  acknowledgedWarnings: WarningCode[]
  reasonCode: string
  note: string | null
}
export interface RowResultSummary { rowId: string; outcome: RowOutcome; code: string | null; error?: string }
export type DecideResult =
  | { mode: 'keep'; decisionId: string; kept: number }
  | { mode: 'inline'; decisionId: string; results: RowResultSummary[]; summary: Partial<Record<RowOutcome, number>>; remaining: number }
  | { mode: 'queued'; decisionId: string; total: number; blocked: number }

export interface UndoRowPlan {
  rowId: string
  verdict: 'will_undo' | 'refused'
  refusalCode: string | null
  before: FieldValues
  after: FieldValues
  money: RowMoney | null
  warnings: WarningCode[]
  planHash: string
}
export interface OpenPayoutRef { payoutId: string; affiliateId: string | null; currency: string | null; amount: number | null; rowCount: number }
export interface UndoPreview {
  issueId: string
  decisionId: string
  rowIds: string[]
  previewToken: string
  counts: { willUndo: number; refused: number }
  refusalsByCode: Record<string, number>
  warnings: WarningCode[]
  payouts: OpenPayoutRef[]
  totals: { byCurrency: CurrencyTotal[]; byAffiliate: AffiliateTotal[] }
  rows: UndoRowPlan[]
}
export interface UndoBody {
  decisionId: string
  rowIds: string[]
  previewToken: string
  acknowledgedWarnings: WarningCode[]
  note: string
}

interface Envelope<T> { success?: boolean; data: T; error?: string; message?: string }
async function unwrap<T>(p: Promise<unknown>): Promise<T> {
  const res = (await p) as Envelope<T>
  if (!res?.success) throw new Error(res?.error || res?.message || 'Request failed')
  return res.data
}

const BASE = '/admin/data-issues'
const issuePath = (id: string) => `${BASE}/${encodeURIComponent(id)}`
/** Inline decisions run up to 30 s server-side (INLINE_BUDGET_MS). */
const LONG = { timeout: 60_000 }

export const adminDataIssuesApi = {
  count: async (type?: string): Promise<number> =>
    (await unwrap<{ open: number }>(apiClient.get(`${BASE}/count`, type ? { params: { type } } : undefined))).open,
  types: () => unwrap<DataIssueTypesInfo>(apiClient.get(`${BASE}/types`)),
  assignees: () => unwrap<DataIssueAssignees>(apiClient.get(`${BASE}/assignees`)),
  list: (q: DataIssueListQuery) => unwrap<DataIssueListPage>(apiClient.get(BASE, { params: q })),
  create: (body: ManualIssueBody) => unwrap<DataIssueDetail>(apiClient.post(BASE, body)),
  get: (id: string) => unwrap<DataIssueDetail>(apiClient.get(issuePath(id))),
  rows: (id: string, q: DataIssueRowsQuery) => unwrap<DataIssueRowsPage>(apiClient.get(`${issuePath(id)}/rows`, { params: q })),
  events: (id: string, q: DataIssueEventsQuery) =>
    unwrap<DataIssueEventsPage>(apiClient.get(`${issuePath(id)}/events`, { params: q })),
  patch: (id: string, body: { assigneeUserId?: string | null; githubIssue?: number | null }) =>
    unwrap<DataIssueDetail>(apiClient.patch(issuePath(id), body)),
  preview: (id: string, body: PreviewBody) => unwrap<DecisionPreview>(apiClient.post(`${issuePath(id)}/preview`, body, LONG)),
  decide: (id: string, body: DecideBody) => unwrap<DecideResult>(apiClient.post(`${issuePath(id)}/decide`, body, LONG)),
  stop: (id: string, body: { decisionId: string; note: string }) =>
    unwrap<{ decisionId: string; halted: number }>(apiClient.post(`${issuePath(id)}/stop`, body)),
  undoPreview: (id: string, body: { decisionId: string; rowIds?: string[] }) =>
    unwrap<UndoPreview>(apiClient.post(`${issuePath(id)}/undo/preview`, body, LONG)),
  undo: (id: string, body: UndoBody) => unwrap<DecideResult>(apiClient.post(`${issuePath(id)}/undo`, body, LONG)),
  reopen: (id: string, body: { rowIds: string[]; reasonCode: string; note: string }) =>
    unwrap<{ reopened: number }>(apiClient.post(`${issuePath(id)}/reopen`, body)),
}
